import React, { useState, useEffect } from 'react';
import {
  Typography,
  TextField,
  Button,
  Box,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Link,
  Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { YandexIcon, GoogleIcon } from '../components/Common/OAuthIcons';
import { isBrigadeDomain } from '../utils/domain';
import Recaptcha from '../components/Common/Recaptcha';

const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register, isAuthenticated } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    confirmPassword: '',
    referral_code: '',
    captcha_token: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({});
  const [captchaPassed, setCaptchaPassed] = useState(false);
  const [captchaOptional, setCaptchaOptional] = useState(false); // Для отладки - сделать капчу опциональной

  // Если пользователь уже аутентифицирован - перенаправляем на главную
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/home', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Очищаем ошибку при вводе
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: false }));
    }
    if (error) {
      setError('');
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, boolean> = {};
    
    if (!formData.email.trim()) {
      errors.email = true;
    }
    
    if (!formData.name.trim()) {
      errors.name = true;
    }
    
    if (!formData.password) {
      errors.password = true;
    } else if (formData.password.length < 6) {
      setError(t('password_too_short'));
      return false;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError(t('passwords_do_not_match'));
      return false;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Проверка капчи (опционально - можно отключить для тестирования)
    if (!captchaOptional && !captchaPassed) {
      setError('Please complete the captcha');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await register({
        email: formData.email,
        name: formData.name,
        password: formData.password,
        referral_code: formData.referral_code || undefined,
        captcha_token: captchaPassed ? formData.captcha_token : undefined
      });

      if (result.success) {
        // Brigade-продукт: регистрация ведёт сразу в FSM-онбординг
        // (панель «Создайте организацию» показывается в FSMLayout).
        // Маркетинговый продукт: профиль для заполнения телефона.
        if (isBrigadeDomain()) {
          navigate('/fsm/board', { replace: true });
        } else {
          navigate('/profile/edit', { replace: true });
        }
      } else {
        setError(result.error || t('registration_failed'));
      }
    } catch (err) {
      setError(t('registration_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCaptchaVerify = (token: string) => {
    setFormData(prev => ({ ...prev, captcha_token: token }));
    setCaptchaPassed(true);
    setError('');
  };

  const handleCaptchaError = (err: string) => {
    console.error('Captcha error:', err);
    // Если капча не загрузилась, можно сделать её опциональной
    if (!captchaOptional) {
      setError('Error loading captcha. Please refresh the page.');
    }
  };

  const handleYandexRegister = () => {
    const redirectPath = isBrigadeDomain() ? '/fsm/board' : '/profile/edit';
    const redirectUrl = encodeURIComponent(window.location.origin + redirectPath);
    window.location.href = `/api/v1/auth/yandex?redirect=${redirectUrl}`;
  };

  const handleGoogleRegister = () => {
    const redirectPath = isBrigadeDomain() ? '/fsm/board' : '/profile/edit';
    const redirectUrl = encodeURIComponent(window.location.origin + redirectPath);
    window.location.href = `/api/v1/auth/google?redirect=${redirectUrl}`;
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
      <Card sx={{ minWidth: 400 }}>
        <CardContent>
          <Typography variant="h5" align="center" gutterBottom>
            {t('register')}
          </Typography>

{error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={t('name')}
              name="name"
              value={formData.name}
              onChange={handleChange}
              margin="normal"
              required
              disabled={loading}
              error={validationErrors.name}
              helperText={validationErrors.name ? t('required_field') : ''}
            />

            <TextField
              fullWidth
              label={t('email')}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              margin="normal"
              required
              disabled={loading}
              error={validationErrors.email}
              helperText={validationErrors.email ? t('required_field') : ''}
            />

            <TextField
              fullWidth
              label={t('password')}
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              margin="normal"
              required
              disabled={loading}
              error={validationErrors.password}
              helperText={validationErrors.password ? t('required_field') : ''}
            />

            <TextField
              fullWidth
              label={t('confirm_password')}
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              margin="normal"
              required
              disabled={loading}
            />

            <TextField
              fullWidth
              label={t('referral_code_optional')}
              name="referral_code"
              value={formData.referral_code}
              onChange={handleChange}
              margin="normal"
              disabled={loading}
            />

            {/* Google reCAPTCHA */}
            <Recaptcha
              onVerify={handleCaptchaVerify}
              onError={handleCaptchaError}
              language="ru"
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 2 }}
              disabled={loading || (!captchaOptional && !captchaPassed)}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  {t('registering')}...
                </>
              ) : t('register')}
            </Button>
          </form>

          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="textSecondary">
              {t('orContinueWithEmail')}
            </Typography>
          </Divider>

          <Button
            variant="outlined"
            fullWidth
            onClick={handleYandexRegister}
            disabled={loading}
            startIcon={<YandexIcon />}
            sx={{ mb: 1 }}
          >
            {t('continueWithYandex')}
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleGoogleRegister}
            disabled={loading}
            startIcon={<GoogleIcon />}
          >
            {t('continueWithGoogle')}
          </Button>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              {t('already_have_account')}{' '}
              <Link component="a" href="/login" underline="hover">{t('login')}</Link>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Register;
